import {groupBy} from '@benchling/js-utils';

import {ProcedureEdgeType} from 'graphql-client/__generated/graphqlGlobalTypes';

export interface FlowchartEdgeData {
  type: ProcedureEdgeType;
  sourceId: string;
  targetId: string;
}

export interface FlowchartNodeData {
  id: string;
  materialInputSettings: Array<{version: {id: string}}>;
  materialOutputSettings: Array<{version: {id: string}}>;
}

export interface FlowchartVersionData {
  edges: Array<FlowchartEdgeData>;
  nodes: Array<FlowchartNodeData>;
}

export interface LinkedPMEI {
  id: string;
  templateMethodVersionId: string;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
}

interface MaterialLinkIndexConfig {
  getSettings: (node: FlowchartNodeData) => Array<{version: {id: string}}>;
  getEdgeNodeVersionId: (edge: FlowchartEdgeData) => string;
  getIndexKey: (edge: FlowchartEdgeData) => string;
}

function buildMaterialLinkIndex(
  edges: Array<FlowchartEdgeData>,
  nodes: Array<FlowchartNodeData>,
  linkedPMEIs: Array<LinkedPMEI>,
  {getSettings, getEdgeNodeVersionId, getIndexKey}: MaterialLinkIndexConfig
): Map<string, Array<LinkedPMEI>> {
  const versionToNodeId = new Map<string, string>();
  for (const node of nodes) {
    for (const setting of getSettings(node)) {
      versionToNodeId.set(setting.version.id, node.id);
    }
  }

  const pmeisByMethodVersionId = groupBy(linkedPMEIs, (pmei) => pmei.templateMethodVersionId);

  const index = new Map<string, Array<LinkedPMEI>>();
  for (const edge of edges) {
    if (edge.type !== ProcedureEdgeType.MATERIAL_EDGE) {
      continue;
    }
    const nodeId = versionToNodeId.get(getEdgeNodeVersionId(edge));
    if (nodeId == null) {
      continue;
    }
    // Accumulate rather than overwrite: one material version can be linked by multiple edges to
    // different method nodes (an output feeding two downstream methods, or an input fed by two
    // upstream methods). Each edge contributes its node's PMEIs to the same index key.
    const existing = index.get(getIndexKey(edge)) ?? [];
    const merged = [...existing, ...(pmeisByMethodVersionId[nodeId] ?? [])];
    index.set(getIndexKey(edge), [...new Map(merged.map((p) => [p.id, p])).values()]);
  }

  return index;
}

/**
 * Builds an index mapping material edges to the corresponding downstream linked PMEIs.
 */
export function buildDownstreamIndex(
  edges: Array<FlowchartEdgeData>,
  nodes: Array<FlowchartNodeData>,
  nextPMEIs: Array<LinkedPMEI>
): Map<string, Array<LinkedPMEI>> {
  return buildMaterialLinkIndex(edges, nodes, nextPMEIs, {
    getSettings: (node) => node.materialInputSettings,
    getEdgeNodeVersionId: (edge) => edge.targetId,
    getIndexKey: (edge) => edge.sourceId,
  });
}

/**
 * Builds an index mapping material edges to the corresponding upstream linked PMEIs.
 */
export function buildOriginIndex(
  edges: Array<FlowchartEdgeData>,
  nodes: Array<FlowchartNodeData>,
  previousPMEIs: Array<LinkedPMEI>
): Map<string, Array<LinkedPMEI>> {
  return buildMaterialLinkIndex(edges, nodes, previousPMEIs, {
    getSettings: (node) => node.materialOutputSettings,
    getEdgeNodeVersionId: (edge) => edge.sourceId,
    getIndexKey: (edge) => edge.targetId,
  });
}
